import React from "react";
import { Card } from "./ui/card";
import {
  Sparkles, AlertTriangle, ShieldCheck, Activity, CheckCircle2, Clock,
} from "lucide-react";

// ---------------------------------------------------------------------------
// AIAnalysisPreview — shown on the "Review & pay" step so patients see the
// platform has already pre-analysed their symptoms before they commit to pay.
// Reuses the same red-flag payload fetched during step 2.
//
// Props:
//   rf = { is_emergency, severity, flags, advice, source, model }
//   symptoms = current symptoms string
//   priority = "standard" | "urgent"
// ---------------------------------------------------------------------------

const SEVERITY_CONFIG = {
  high:     { label: "High",     color: "bg-rose-500",  text: "text-rose-700",  bg: "bg-rose-50 border-rose-200",  pct: 85 },
  moderate: { label: "Moderate", color: "bg-amber-500", text: "text-amber-700", bg: "bg-amber-50 border-amber-200", pct: 55 },
  medium:   { label: "Moderate", color: "bg-amber-500", text: "text-amber-700", bg: "bg-amber-50 border-amber-200", pct: 55 },
  low:      { label: "Low",      color: "bg-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200", pct: 25 },
};

export default function AIAnalysisPreview({ rf, symptoms, priority }) {
  if (!rf || rf.source === "idle") {
    return (
      <Card className="p-5 border-slate-200 bg-gradient-to-br from-slate-50 to-blue-50/40" data-testid="ai-preview-empty">
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Sparkles className="w-4 h-4"/> Add more detail in symptoms to see AI pre-analysis.
        </div>
      </Card>
    );
  }

  const sev = SEVERITY_CONFIG[rf.severity] || SEVERITY_CONFIG.low;
  const flags = Array.isArray(rf.flags) ? rf.flags.filter(Boolean) : [];
  const wordCount = (symptoms || "").trim().split(/\s+/).filter(Boolean).length;

  return (
    <Card
      className="p-5 sm:p-6 border-[#0B5ED7]/30 bg-gradient-to-br from-white via-blue-50/30 to-[#20C997]/5 relative overflow-hidden"
      data-testid="ai-analysis-preview"
    >
      {/* Shimmer decoration */}
      <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[radial-gradient(circle,rgba(32,201,151,0.15),transparent_60%)]" />
      <div className="relative">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#0B5ED7] to-[#20C997] text-white grid place-items-center">
            <Sparkles className="w-4.5 h-4.5"/>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#0B5ED7]">AI pre-analysis</div>
            <div className="font-heading font-semibold text-slate-900 text-base leading-tight">
              Here's what our AI is seeing
            </div>
          </div>
        </div>

        {/* Severity gauge */}
        <div className="mt-5 flex items-start gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <div className="flex items-baseline justify-between text-xs text-slate-500">
              <span>Severity signal</span>
              <span className={`font-heading font-bold text-lg ${sev.text}`}>{sev.label}</span>
            </div>
            <div className="mt-1.5 h-2.5 rounded-full bg-slate-200 overflow-hidden">
              <div
                className={`h-full ${sev.color} transition-[width] duration-700 ease-out rounded-full`}
                style={{ width: `${sev.pct}%` }}
              />
            </div>
            <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-500">
              <Activity className="w-3 h-3"/>
              <span>Analysed {wordCount} words of symptom description</span>
            </div>
          </div>

          {rf.is_emergency && (
            <div className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-rose-800 text-xs font-medium inline-flex items-center gap-2 shrink-0">
              <AlertTriangle className="w-4 h-4"/> Priority pattern detected
            </div>
          )}
        </div>

        {/* Red flags */}
        {flags.length > 0 && (
          <div className={`mt-5 rounded-xl border ${sev.bg} p-4`}>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-600 flex items-center gap-1.5">
              <AlertTriangle className={`w-3 h-3 ${sev.text}`}/> Potential red flags
            </div>
            <ul className="mt-2 space-y-1.5">
              {flags.slice(0, 5).map((f, i) => (
                <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                  <span className={`mt-1.5 w-1.5 h-1.5 rounded-full ${sev.color} shrink-0`}/>
                  {f}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Advice */}
        {rf.advice && (
          <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-700 flex items-center gap-1.5">
              <CheckCircle2 className="w-3 h-3"/> Recommended next step
            </div>
            <p className="mt-2 text-sm text-slate-800 leading-relaxed">{rf.advice}</p>
          </div>
        )}

        {/* Tier suggestion */}
        {rf.is_emergency && priority !== "urgent" && (
          <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 flex items-start gap-2">
            <Clock className="w-4 h-4 mt-0.5 shrink-0"/>
            <div>
              AI suggests the <b>Priority or Video Consultation</b> tier for priority review — your case will be reviewed within 24 hrs.
              You can change priority using the Back button.
            </div>
          </div>
        )}

        {/* Disclaimer */}
        <div className="mt-5 pt-4 border-t border-slate-200 flex items-start gap-2 text-[11px] text-slate-500 leading-relaxed">
          <ShieldCheck className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0"/>
          <span>
            This is an AI-assisted preview only — your final written opinion comes from a verified human neurologist / neurosurgeon.
            {rf.model && <> <span className="font-mono text-slate-400">· {rf.model}</span></>}
          </span>
        </div>
      </div>
    </Card>
  );
}
